import React, { useEffect, useState } from 'react';
import { Auth } from '@supabase/auth-ui-react';
import { ThemeSupa } from '@supabase/auth-ui-shared';
import { supabase } from '../supabaseClient';
import { useNavigate, useLocation } from 'react-router-dom';
import './AuthComponent.css'; // We'll create this CSS file

export default function AuthComponent() {
  const [session, setSession] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isSignUp, setIsSignUp] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  // Check URL parameters on mount to determine if we should show sign up by default
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('signup') === 'true') {
      setIsSignUp(true);
    }
  }, [location]);

  useEffect(() => {
    const checkAndInsertUser = async (session) => {
      if (!session) return;

      const user = session.user;

      try {
        // Check if user has a profile already
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', user.id)
          .limit(1);
        
        const hasProfile = profileData && profileData.length > 0;
        
        // Check if the user already exists in the `users` table
        const { data: existing, error: existingError } = await supabase
          .from('users')
          .select('id, submitted_profile')
          .eq('id', user.id)
          .single();

        if (existingError && existingError.code !== 'PGRST116') {
          console.error("Error checking user:", existingError);
          return;
        }

        if (!existing) {
          // Create new user - remove created_at and last_login as they should be auto-generated
          console.log("Inserting new user:", user.email);
          const { error: insertError } = await supabase.from('users').insert([
            {
              id: user.id,
              email: user.email,
              is_edu: user.email.endsWith('.edu'),
              submitted_profile: hasProfile,
            }
          ]);
          if (insertError) {
            console.error("Error inserting user:", insertError);
          }
        } else if (hasProfile && !existing.submitted_profile) {
          // User exists but flag needs to be updated to match profile existence
          console.log("Updating user submission status to true:", user.id);
          const { error: updateError } = await supabase
            .from('users')
            .update({ submitted_profile: true })
            .eq('id', user.id);
            
          if (updateError) {
            console.error("Error updating user submission status:", updateError);
          }
        }
      } catch (err) {
        console.error("Error in user profile check:", err);
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      checkAndInsertUser(session);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      checkAndInsertUser(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const getErrorMessage = (error) => {
    if (error.includes('Invalid login credentials')) {
      return 'Invalid email or password. Please check your credentials and try again.';
    }
    if (error.includes('Email not confirmed')) {
      return 'Please check your email and click the confirmation link before signing in.';
    }
    if (error.includes('User already registered')) {
      return 'An account with this email already exists. Try signing in instead.';
    }
    if (error.includes('Password should be at least')) {
      return 'Password must be at least 6 characters long.';
    }
    if (error.includes('created_at')) {
      return 'Account creation in progress. Please wait a moment and try again.';
    }
    if (error.includes('schema cache')) {
      return 'System temporarily unavailable. Please try again in a few moments.';
    }
    return error;
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/account`
        }
      });
      
      if (error) throw error;
    } catch (error) {
      setError(getErrorMessage(error.message));
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (showForgotPassword) {
        // Forgot password flow - fix the redirect URL
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });

        if (error) throw error;
        
        setSuccess('Password reset email sent! Check your inbox for instructions.');
        setShowForgotPassword(false);
      } else if (isSignUp) {
        // Sign up flow
        const { data, error } = await supabase.auth.signUp({
          email,
          password
        });

        if (error) throw error;
        
        if (data.user && !data.user.email_confirmed_at) {
          setSuccess('Account created! Please check your email to confirm your account before signing in.');
        } else {
          navigate('/account');
        }
      } else {
        // Sign in flow
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;
        
        // Redirect based on where they came from or to default page
        const from = location.state?.from || '/account';
        navigate(from);
      }
    } catch (error) {
      setError(getErrorMessage(error.message));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setError(null);
    setSuccess(null);
    setShowForgotPassword(false);
  };

  const toggleMode = (newMode) => {
    resetForm();
    if (newMode === 'forgot') {
      setShowForgotPassword(true);
      setIsSignUp(false);
    } else if (newMode === 'signup') {
      setIsSignUp(true);
      setShowForgotPassword(false);
    } else {
      setIsSignUp(false);
      setShowForgotPassword(false);
    }
  };

  if (!session) {
    return (
      <div className={`auth-container ${isSignUp ? 'signup-mode' : showForgotPassword ? 'forgot-mode' : 'signin-mode'}`}>
        <div className="auth-card">
          <div className="auth-header">
            <h2>
              {showForgotPassword ? 'Reset Password' : isSignUp ? 'Create an Account' : 'Welcome Back'}
            </h2>
            <p className="auth-subheader">
              {showForgotPassword 
                ? 'Enter your email address and we\'ll send you a reset link'
                : isSignUp 
                ? 'Join College Arena and compare your profile with others!' 
                : 'Sign in to access your account and continue your journey'}
            </p>
          </div>

          {error && <div className="auth-error">{error}</div>}
          {success && <div className="auth-success">{success}</div>}
          
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="your@email.com"
                disabled={loading}
              />
            </div>
            
            {!showForgotPassword && (
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder={isSignUp ? "Create a strong password" : "Enter your password"}
                  minLength={isSignUp ? 6 : undefined}
                  disabled={loading}
                />
              </div>
            )}
            
            {isSignUp && (
              <div className="edu-disclaimer">
                <p>📧 We recommend you sign up with your .edu email address, as you may not be able to submit your essays without it!</p>
              </div>
            )}
            
            <button 
              type="submit" 
              disabled={loading}
              className={`auth-submit ${showForgotPassword ? 'forgot-button' : isSignUp ? 'signup-button' : 'signin-button'}`}
            >
              {loading 
                ? 'Processing...' 
                : showForgotPassword 
                ? 'Send Reset Email' 
                : isSignUp 
                ? 'Create Account' 
                : 'Sign In'}
            </button>
          </form>

          {!showForgotPassword && (
            <div className="social-auth-bottom">
              <div className="auth-divider">
                <span>or</span>
              </div>
              
              <button 
                onClick={handleGoogleSignIn}
                disabled={loading}
                className="google-signin-button"
              >
                <div className="google-signin-content">
                  <svg className="google-icon" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span className="google-signin-text">Continue with Google</span>
                </div>
              </button>
            </div>
          )}
          
          <div className="auth-footer">
            {showForgotPassword ? (
              <p>
                Remember your password?
                <button 
                  type="button"
                  className="auth-toggle-button"
                  onClick={() => toggleMode('signin')}
                >
                  Sign In
                </button>
              </p>
            ) : (
              <>
                <p>
                  {isSignUp 
                    ? 'Already have an account?' 
                    : "Don't have an account?"}
                  <button 
                    type="button"
                    className="auth-toggle-button"
                    onClick={() => toggleMode(isSignUp ? 'signin' : 'signup')}
                  >
                    {isSignUp ? 'Sign In' : 'Sign Up'}
                  </button>
                </p>
                {!isSignUp && (
                  <p>
                    <button 
                      type="button"
                      className="auth-toggle-button forgot-link"
                      onClick={() => toggleMode('forgot')}
                    >
                      Forgot your password?
                    </button>
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ textAlign: 'center', marginTop: '20px' }}>
      <p>Redirecting to your account...</p>
    </div>
  );
}
